// Gold: private ASX dashboard. Sign in, enter an ASX code, and browse its public data in tabs.
// Routes live in the hash: #/BHP/chart. Data per stock is fetched once per visit, tab by tab.
import type { IChartApi } from "lightweight-charts";
import { api, ApiError, forgotUrl, supabase } from "./api";
import { barChart, priceChart, sparkline, type Bar, type PriceChartOptions } from "./charts";
import { codePill, compact, date, dateTime, dayLabel, esc, fixed, humanize, money, n, pct, price, stockLink, sydneyDate, tone, getMarks } from "./format";
import { assessPanel, riskWord, starsHtml } from "./assess";
import { renderChat } from "./chat";
import { renderAnalytics } from "./analytics";
import { renderLeads } from "./leads";
import { renderInbox } from "./inbox";
import { calendarPanel, docsPanel } from "./calendar";
import { renderReviews } from "./reviews";
import { homeMarketCard, renderMarket } from "./market";
import { renderWatchlists } from "./watchlists";
import { renderSim } from "./sim";
import { renderJoin } from "./join";
import { installQuickMenu, openBlacklist, openWatch, refreshMarks } from "./stockmenu";
import { renderSettings } from "./settings";
import { askClaudeUrl, renderConnect } from "./connector";
import { markdown } from "./markdown";
import { loadPortfolio, renderPortfolio, type Holding } from "./portfolio";
import { signals, type StockData } from "./health";

import { version } from "../package.json";

const app = document.getElementById("app")!;
document.getElementById("version")!.textContent = `v${version}`;
const search = document.getElementById("search") as HTMLFormElement;
const signOut = document.getElementById("signout") as HTMLButtonElement;
const refreshButton = document.getElementById("refresh") as HTMLButtonElement;

// ---------- data ----------

type Part = "prices" | "profile" | "financials" | "announcements";
interface SourceStatus { source: string; ok: boolean; stale: boolean; fetchedAt: string | null; error?: string; keys?: string[] }
interface Loaded<T = any> { data: T | null; sources: SourceStatus[]; error?: string }

const store = new Map<string, Partial<Record<Part, Promise<Loaded>>>>();

function load<T = any>(code: string, part: Part): Promise<Loaded<T>> {
  let s = store.get(code);
  if (!s) store.set(code, (s = {}));
  s[part] ??= api<Loaded>(`stock/${code}/${part}`).catch((e: Error) => {
    delete s![part]; // let a later visit retry
    if (e instanceof ApiError && e.status === 401) renderSignIn();
    return { data: null, sources: [], error: e.message };
  });
  return s[part] as Promise<Loaded<T>>;
}

let watchlist: string[] = [];
async function refreshWatchlist(method = "GET", code?: string) {
  const r = await api<{ codes: string[] }>(code ? `watchlist/${code}` : "watchlist", method);
  watchlist = r.codes;
}

// ---------- shared bits ----------

const spinner = `<div class="loading" role="status"><span></span>Loading…</div>`;
const notice = (msg: string, kind = "warn") => `<div class="notice ${kind}">${esc(msg)}</div>`;
const card = (title: string, body: string, extra = "") => `<section class="card ${extra}"><h3>${esc(title)}</h3>${body}</section>`;
const stat = (label: string, value: string, cls = "") => `<div class="stat"><dt>${esc(label)}</dt><dd class="${cls}">${value}</dd></div>`;

const SOURCE_NAMES: Record<string, string> = {
  "yahoo-chart": "Yahoo Finance: prices and dividends",
  "stooq-prices": "Stooq: prices (backup)",
  "yahoo-summary": "Yahoo Finance: profile, valuation, analysts",
  "asx-header": "ASX: company header",
  "asx-key-statistics": "ASX: key statistics",
  "yahoo-financials": "Yahoo Finance: annual financials",
  "asx-announcements": "ASX: announcements",
};
const sourceName = (id: string) => SOURCE_NAMES[id] ?? id;

function sourceProblems(r: Loaded): string {
  if (r.error) return notice(r.error, "error");
  return r.sources.filter((s) => !s.ok).map((s) =>
    notice(s.stale ? `${sourceName(s.source)} couldn't be refreshed, so this shows data from ${dateTime(s.fetchedAt)}.` : `${sourceName(s.source)} is unavailable right now (${s.error ?? "no data"}).`),
  ).join("");
}

// ---------- sign in ----------

function renderSignIn(message = "") {
  document.body.classList.add("signed-out");
  document.body.classList.remove("in-sim", "sim-user");
  app.innerHTML = `
    <form class="signin card" id="signin">
      <h1>Gold</h1>
      <p class="muted">Sign in with your Gold login or your stobsy staff account.</p>
      <label>Email or username<input name="login" type="text" autocomplete="username" autocapitalize="none" spellcheck="false" required></label>
      <label>Password<input name="password" type="password" autocomplete="current-password" required></label>
      <div class="error" id="signin-error">${esc(message)}</div>
      <button type="submit" class="primary">Sign in</button>
      <p class="small muted" id="forgot-line">Forgot your password? Simulator logins: ask the person who invited you for a new link. Staff: <a href="${esc(forgotUrl())}">reset it here</a>.</p>
    </form>`;
  const form = document.getElementById("signin") as HTMLFormElement;
  // Self-service reset for Gold logins, once the websites admin has set up Turnstile.
  api<{ turnstileSiteKey: string | null }>("auth/config").then(({ turnstileSiteKey }) => {
    if (turnstileSiteKey) {
      document.getElementById("forgot-line")!.innerHTML = `<button type="button" class="linkish" id="forgot">Forgot password?</button> · Staff: <a href="${esc(forgotUrl())}">reset here</a>`;
      document.getElementById("forgot")!.addEventListener("click", () => renderForgot(turnstileSiteKey));
    }
  }).catch(() => {});
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const f = new FormData(form);
    const button = form.querySelector<HTMLButtonElement>("button[type=submit]")!;
    button.disabled = true;
    const login = String(f.get("login")).trim(), password = String(f.get("password"));
    const err = document.getElementById("signin-error")!;
    try {
      // A Gold login first (email or username, checked by Gold itself), then the staff sign-in.
      const r = await api<{ gold: boolean }>("auth/login", "POST", { login, password });
      if (r.gold) { button.disabled = false; return start(); }
    } catch (e) {
      button.disabled = false;
      err.textContent = (e as Error).message;
      return;
    }
    if (!login.includes("@")) { button.disabled = false; err.textContent = "That login and password don't match."; return; }
    const { error } = await supabase.auth.signInWithPassword({ email: login, password });
    button.disabled = false;
    if (error) err.textContent = error.message === "Invalid login credentials" ? "That login and password don't match." : error.message;
    else start();
  });
}

// "Forgot password?" for Gold logins: a Turnstile check, then Gold emails a password link to the
// login's confirmed address (the same answer either way, so nobody can test who has a login).
function renderForgot(siteKey: string) {
  app.innerHTML = `
    <form class="signin card" id="forgot-form">
      <h1>Forgot password</h1>
      <p class="muted">Enter your email or username and we'll email you a link to choose a new password.</p>
      <label>Email or username<input name="login" type="text" autocomplete="username" autocapitalize="none" spellcheck="false" required></label>
      <div id="ts"></div>
      <div class="error" id="forgot-error"></div>
      <button type="submit" class="primary">Email me a link</button>
      <a href="#/" id="back">Back to sign in</a>
    </form>`;
  document.getElementById("back")!.addEventListener("click", (e) => { e.preventDefault(); renderSignIn(); });
  let widget: string | undefined;
  const w = window as unknown as { turnstile?: { render: (el: string, o: object) => string; getResponse: (id?: string) => string; reset: (id?: string) => void } };
  const draw = () => { widget = w.turnstile!.render("#ts", { sitekey: siteKey }); };
  if (w.turnstile) draw();
  else {
    const sc = document.createElement("script");
    sc.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    sc.async = true;
    sc.onload = draw;
    document.head.appendChild(sc);
  }
  const form = document.getElementById("forgot-form") as HTMLFormElement;
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const err = document.getElementById("forgot-error")!;
    const b = form.querySelector<HTMLButtonElement>("button[type=submit]")!;
    b.disabled = true;
    try {
      await api("auth/forgot", "POST", { login: new FormData(form).get("login"), turnstile: w.turnstile?.getResponse(widget) });
      form.innerHTML = `<h1>Check your email</h1><p>If that's a Gold login with a confirmed email, a link to choose a new password is on its way. It works once and lasts 7 days.</p><a href="#/" id="back2">Back to sign in</a>`;
      document.getElementById("back2")!.addEventListener("click", (x) => { x.preventDefault(); renderSignIn(); });
    } catch (x) {
      err.textContent = (x as Error).message;
      b.disabled = false;
      w.turnstile?.reset(widget);
    }
  });
}

signOut.addEventListener("click", async () => {
  if (simUser) await api("auth/logout", "POST", {}).catch(() => {});
  else await supabase.auth.signOut();
  simUser = false;
  document.body.classList.remove("sim-user");
  store.clear();
  renderSignIn();
});

// ---------- home ----------

async function renderHome() {
  current = null;
  app.innerHTML = `
    <section class="home">
      <div id="home-summary"></div>
      <div id="attention"></div>
      <div id="coming"></div>
      <div id="market-home"></div>
      <h3 class="section-title">Watchlist <a class="small" href="#/watchlists">All watchlists →</a></h3>
      <div id="watch">${spinner}</div>
    </section>`;
  const el = document.getElementById("watch")!;
  // Your portfolio at a glance (the same figures as the Portfolio page), linking to it.
  loadPortfolio().then((p) => {
    const box = document.getElementById("home-summary");
    if (!box || !p.holdings.length) {
      if (box) box.innerHTML = `<p class="muted">Type a code in the search box (for example BHP) to look up any ASX company.</p>`;
      return;
    }
    const isToday = (h: Holding) => dayLabel(h.priceDate) === "today";
    const day = p.holdings.reduce((a, h) => a + (isToday(h) && h.value != null && h.dayChangePct != null ? h.value - h.value / (1 + h.dayChangePct) : 0), 0);
    const signed = (v: number) => `${v > 0 ? "+" : ""}${money(v)}`;
    box.innerHTML = `<a class="card home-summary" href="#/portfolio">
      <span><small class="muted">Portfolio</small><b class="big">${money(p.totals.value)}</b></span>
      <span><small class="muted">Today</small><b class="${tone(day)}">${signed(day)}</b></span>
      <span><small class="muted">Unrealised</small><b class="${tone(p.totals.unrealised)}">${signed(p.totals.unrealised)} <small>${pct(p.totals.unrealisedPct, true)}</small></b></span>
      <span class="go" aria-hidden="true">›</span></a>`;
  }).catch(() => {});
  // Coming up: the next key dates, catalysts and promises for held and watched stocks.
  api<{ events: { code: string; held: boolean; date: string; title: string; kind: string }[] }>("coming-up").then(({ events }) => {
    const box = document.getElementById("coming");
    if (box && events.length) {
      box.innerHTML = `<section class="card"><h3>Coming up</h3><ul class="plain coming">${events.map((e) => `<li>${stockLink(e.code)}<span><a href="#/${esc(e.code)}/calendar">${esc(e.title)}</a><small class="muted">${date(e.date)}</small></span></li>`).join("")}</ul></section>`;
    }
  }).catch(() => {});
  // Claude's latest market assessment, with the ask buttons.
  homeMarketCard(document.getElementById("market-home")!);
  // Needs attention (from Claude's assessments), and each watchlist stock's latest rating.
  api<{ attention: { code: string; text: string; level: string }[]; ratings: Record<string, { health: number; risk: number }> }>("attention").then((r) => {
    const box = document.getElementById("attention");
    if (box && r.attention.length) {
      box.innerHTML = `<section class="card attention"><h3>Needs attention</h3><ul class="plain">${r.attention.slice(0, 12).map((x) =>
        `<li class="${esc(x.level)}">${stockLink(x.code)}<a href="#/${esc(x.code)}/assess">${esc(x.text)}</a></li>`).join("")}</ul></section>`;
    }
    for (const [code, rating] of Object.entries(r.ratings)) {
      const name = el.querySelector(`[data-code="${code}"] .wl-rating`);
      if (name) name.innerHTML = `${starsHtml(rating.health)}`;
    }
  }).catch(() => {});
  if (!watchlist.length) {
    el.innerHTML = `<p class="muted">Nothing yet. Open a stock and tap ☆ to add it.</p>`;
    return;
  }
  const preview = watchlist.slice(0, 12);
  el.innerHTML = `${watchlist.length > preview.length ? `<p class="muted small">Showing ${preview.length} of ${watchlist.length}. <a href="#/watchlists">See them all, sorted into your lists →</a></p>` : ""}<ul class="watchlist">${preview.map((c) => `<li><a href="#/${c}" data-code="${c}"><b>${codePill(c)}<span class="wl-rating"></span></b><span class="wl-name"></span><span class="wl-spark"></span><span class="wl-price"></span></a></li>`).join("")}</ul>`;
  for (const code of preview) {
    load(code, "prices").then((r) => {
      const a = el.querySelector<HTMLElement>(`[data-code="${code}"]`);
      const p = r.data;
      if (!a || !p?.bars?.length) return;
      const bars: Bar[] = p.bars;
      const prev = p.previousClose ?? bars.at(-2)?.c, last = p.price ?? bars.at(-1)!.c;
      const ch = prev ? last / prev - 1 : null;
      const when = dayLabel(p.priceDate ?? sydneyDate(p.time ?? bars.at(-1)!.t));
      a.querySelector(".wl-name")!.textContent = p.name ?? "";
      a.querySelector(".wl-spark")!.innerHTML = sparkline(bars.slice(-90).map((b) => b.c), 120, 32);
      a.querySelector(".wl-price")!.innerHTML = `${price(last, p.currency ?? "AUD")}<small class="${tone(ch)}">${pct(ch, true)}${when === "today" ? "" : ` ${when}`}</small>`;
    });
  }
}

// ---------- stock page ----------

// Most used first: the picture, Claude's view, what's coming, then the detail.
const TABS = [
  ["overview", "Overview"],
  ["chart", "Chart"],
  ["assess", "Assess"],
  ["calendar", "Calendar"],
  ["announcements", "News"],
  ["financials", "Financials"],
  ["dividends", "Dividends"],
  ["research", "Research"],
  ["docs", "Docs"],
  ["health", "Health"],
  ["sources", "Sources"],
] as const;
type Tab = (typeof TABS)[number][0];

let current: { code: string; tab: Tab; token: number; sim?: true } | null = null;
let simUser = false; // signed in with a Gold login: only the simulator
let chart: IChartApi | null = null;
let renderToken = 0;

async function renderStock(code: string, tab: Tab) {
  const same = current?.code === code;
  current = { code, tab, token: ++renderToken };
  chart?.remove();
  chart = null;
  if (!same) {
    app.innerHTML = `
      <section class="stock-head" id="head">${spinner}</section>
      <nav class="tabs" role="tablist">${TABS.map(([id, label]) => `<a role="tab" href="#/${code}/${id}" data-tab="${id}">${label}</a>`).join("")}</nav>
      <section id="panel" role="tabpanel"></section>`;
    renderHead(code);
  }
  document.querySelectorAll<HTMLElement>(".tabs a").forEach((a) => {
    const on = a.dataset.tab === tab;
    a.classList.toggle("on", on);
    a.setAttribute("aria-selected", String(on));
    if (on) a.scrollIntoView({ block: "nearest", inline: "nearest" });
  });
  const panel = document.getElementById("panel")!;
  panel.innerHTML = spinner;
  const token = renderToken;
  const html = await PANELS[tab](code, panel);
  if (token !== renderToken) return; // the user moved on
  if (typeof html === "string") panel.innerHTML = html;
}

async function renderHead(code: string) {
  const [pr, pf] = await Promise.all([load(code, "prices"), load(code, "profile")]);
  const head = document.getElementById("head");
  if (!head || current?.code !== code) return;
  const p = pr.data, s = pf.data?.summary, h = pf.data?.header;
  if (!p?.bars?.length && !h) {
    head.innerHTML = `<h2>${esc(code)}</h2>${notice(`Couldn't find ${code}. Check the code is listed on the ASX.`, "error")}${sourceProblems(pr)}`;
    return;
  }
  const bars: Bar[] = p?.bars ?? [];
  const cur = p?.currency ?? "AUD";
  const last = p?.price ?? n(h?.priceLast) ?? bars.at(-1)?.c ?? null;
  // The move on the price's own trading day: before a stock trades today (or while the ~20-minute
  // delayed feed catches up) that's the previous session, and it's labelled with its date.
  const prev = p?.previousClose ?? bars.at(-2)?.c;
  const ch = prev && last ? last / prev - 1 : n(s?.price?.regularMarketChangePercent);
  const chAbs = prev && last ? last - prev : n(s?.price?.regularMarketChange);
  const when = dayLabel(p?.priceDate ?? (p?.time ? sydneyDate(p.time) : bars.length ? sydneyDate(bars.at(-1)!.t) : null));
  const name = s?.price?.longName ?? p?.name ?? h?.displayName ?? code;
  const sector = [s?.assetProfile?.sector ?? h?.sector, s?.assetProfile?.industry ?? h?.industryGroup].filter(Boolean).join(" · ");
  const m = getMarks();
  const starred = m.watched.includes(code), held = m.held.includes(code), black = m.blacklisted[code];
  const lists = m.lists[code] ?? [];
  head.innerHTML = `
    <div class="title">
      <div><h2>${esc(name)}</h2><p class="muted">ASX: ${esc(code)}${sector ? ` · ${esc(sector)}` : ""}</p>
        <p class="head-marks">${held ? `<span class="tag held">● In your portfolio</span>` : ""}${starred ? `<span class="tag watched">★ ${lists.length ? esc(lists.join(", ")) : "Watching"}</span>` : ""}</p></div>
      <div class="head-actions">
        <button class="star" id="star" aria-pressed="${starred}" title="${starred ? "Watchlists" : "Add to a watchlist"}" aria-label="${starred ? "Watchlists" : "Add to a watchlist"}">${starred ? "★" : "☆"}</button>
        <button class="star black-btn" id="black" aria-pressed="${!!black}" title="${black ? "Blacklisted" : "Blacklist"}" aria-label="${black ? "Blacklisted" : "Blacklist"}">⊘</button>
      </div>
    </div>
    ${black ? `<div class="notice black-note">⊘ <b>Blacklisted</b>${black.reason ? `: ${esc(black.reason)}` : ""} <span class="muted small">since ${date(black.addedAt)}</span></div>` : ""}
    <div class="quote">
      <span class="last">${price(last, cur)}</span>
      <span class="change ${tone(ch)}">${chAbs != null ? (chAbs > 0 ? "+" : "") + fixed(chAbs, Math.abs(chAbs) < 1 ? 3 : 2) : ""} (${pct(ch, true)}) ${when}</span>
      <span class="muted asof">${p?.time ? `as of ${dateTime(p.time)}` : ""}</span>
    </div>
    <a class="ask" href="${esc(askClaudeUrl(code))}" target="_blank" rel="noopener">Ask Claude to Assess ${esc(code)}</a>`;
  const again = () => { refreshWatchlist().catch(() => {}); renderHead(code); };
  document.getElementById("star")!.addEventListener("click", () => openWatch(code, again));
  document.getElementById("black")!.addEventListener("click", () => openBlacklist(code, again));
}

// ---------- panels ----------

type Panel = (code: string, el: HTMLElement) => Promise<string | void>;

const overview: Panel = async (code) => {
  const [pr, pf] = await Promise.all([load(code, "prices"), load(code, "profile")]);
  const p = pr.data, s = pf.data?.summary ?? {}, h = pf.data?.header ?? {}, st = pf.data?.stats ?? {};
  const cur = p?.currency ?? "AUD";
  const bars: Bar[] = p?.bars ?? [];
  const last = p?.price ?? bars.at(-1)?.c ?? null;
  const sd = s.summaryDetail ?? {}, ks = s.defaultKeyStatistics ?? {}, fd = s.financialData ?? {}, ap = s.assetProfile ?? {};
  const out: string[] = [await positionCard(code)];

  // 52-week range
  const lo = p?.low52 ?? n(sd.fiftyTwoWeekLow), hi = p?.high52 ?? n(sd.fiftyTwoWeekHigh);
  const year = bars.filter((b) => b.t > (bars.at(-1)?.t ?? 0) - 365 * 86400);
  if (year.length > 1) {
    const yr = last && year[0].c ? last / year[0].c - 1 : null;
    const pos = lo != null && hi != null && last != null && hi > lo ? ((last - lo) / (hi - lo)) * 100 : null;
    out.push(card("Past year", `
      <div class="spark-wrap">${sparkline(year.map((b) => b.c))}</div>
      <div class="range"><span>${price(lo, cur)}</span><div class="track">${pos != null ? `<i style="left:${pos.toFixed(1)}%"></i>` : ""}</div><span>${price(hi, cur)}</span></div>
      <p class="muted center">52-week low and high · 1-year change <b class="${tone(yr)}">${pct(yr, true)}</b></p>`));
  }

  const mcap = n(sd.marketCap) ?? n(s.price?.marketCap) ?? n(h.marketCap);
  const yieldV = n(sd.dividendYield) ?? n(sd.trailingAnnualDividendYield);
  out.push(card("Key numbers", `<dl class="stats">
    ${stat("Market cap", money(mcap, "AUD"))}
    ${stat("P/E (trailing)", fixed(n(sd.trailingPE) ?? n(st.priceEarningsRatio), 1))}
    ${stat("P/E (forward)", fixed(n(sd.forwardPE), 1))}
    ${stat("Price / book", fixed(n(ks.priceToBook), 2))}
    ${stat("EPS (trailing)", price(n(ks.trailingEps) ?? n(st.earningsPerShare), cur))}
    ${stat("Dividend yield", pct(yieldV))}
    ${stat("Payout ratio", pct(n(sd.payoutRatio)))}
    ${stat("Beta", fixed(n(sd.beta), 2))}
    ${stat("Avg volume", compact(n(sd.averageVolume) ?? n(sd.averageDailyVolume10Day)))}
    ${stat("Shares on issue", compact(n(ks.sharesOutstanding) ?? n(st.numOfShares)))}
    ${stat("Return on equity", pct(n(fd.returnOnEquity)))}
    ${stat("Profit margin", pct(n(fd.profitMargins)))}
  </dl>`));

  // Analysts
  const target = n(fd.targetMeanPrice);
  const trend = s.recommendationTrend?.trend?.[0];
  if (target != null || trend) {
    const counts = trend ? [["Strong buy", trend.strongBuy], ["Buy", trend.buy], ["Hold", trend.hold], ["Sell", trend.sell], ["Strong sell", trend.strongSell]] as [string, number][] : [];
    const total = counts.reduce((a, [, v]) => a + (v || 0), 0);
    out.push(card("Analysts", `<dl class="stats">
      ${stat("Average target", price(target, cur))}
      ${stat("Target range", target != null ? `${price(n(fd.targetLowPrice), cur)} – ${price(n(fd.targetHighPrice), cur)}` : "–")}
      ${stat("Upside to average", pct(target != null && last ? target / last - 1 : null, true), tone(target != null && last ? target - last : null))}
      ${stat("Consensus", esc(humanize(String(fd.recommendationKey ?? "–"))))}
    </dl>
    ${total ? `<div class="recs">${counts.map(([k, v], i) => v ? `<span class="r${i}" style="flex:${v}" title="${k}: ${v}">${v}</span>` : "").join("")}</div>
      <p class="muted small">${counts.map(([k, v]) => `${k} ${v || 0}`).join(" · ")}</p>` : ""}`));
  }

  // Upcoming dates
  const ce = s.calendarEvents ?? {};
  const earnings = ce.earnings?.earningsDate?.[0];
  if (earnings || ce.exDividendDate || ce.dividendDate) {
    out.push(card("Dates", `<dl class="stats">
      ${stat("Next results", earnings ? date(n(earnings)) : "–")}
      ${stat("Ex-dividend", ce.exDividendDate ? date(n(ce.exDividendDate)) : "–")}
      ${stat("Dividend paid", ce.dividendDate ? date(n(ce.dividendDate)) : "–")}
    </dl>`));
  }

  // About
  const about = ap.longBusinessSummary;
  if (about || ap.website) {
    const officers = (ap.companyOfficers ?? []).slice(0, 4).map((o: any) => `<li>${esc(o.name)} <span class="muted">${esc(o.title)}</span></li>`).join("");
    out.push(card("About", `
      ${about ? `<details><summary>${esc(String(about).slice(0, 220))}${String(about).length > 220 ? "…" : ""}</summary><p>${esc(about)}</p></details>` : ""}
      <dl class="stats">
        ${ap.website ? stat("Website", `<a href="${esc(ap.website)}" target="_blank" rel="noopener">${esc(String(ap.website).replace(/^https?:\/\/(www\.)?/, ""))}</a>`) : ""}
        ${ap.fullTimeEmployees ? stat("Employees", compact(n(ap.fullTimeEmployees))) : ""}
        ${ap.city ? stat("Head office", esc([ap.city, ap.state, ap.country].filter(Boolean).join(", "))) : ""}
      </dl>
      ${officers ? `<h4>Leadership</h4><ul class="plain">${officers}</ul>` : ""}`));
  }

  // Everything ASX reports, whatever its field names are
  const asxRows = Object.entries({ ...h, ...st }).filter(([, v]) => v != null && v !== "" && typeof v !== "object");
  if (asxRows.length) {
    out.push(card("ASX statistics", `<details><summary>${asxRows.length} figures from asx.com.au</summary>
      <dl class="stats">${asxRows.map(([k, v]) => stat(humanize(k), esc(typeof v === "number" ? v.toLocaleString("en-AU") : v))).join("")}</dl></details>`));
  }
  return `<div class="grid">${out.join("")}</div>${sourceNote(code, [pr, pf])}`;
};

// Feeds that failed, as one quiet line (the overview still shows the saved figures); details on Sources.
function sourceNote(code: string, parts: Loaded[]) {
  if (parts.some((r) => r.error)) return parts.map(sourceProblems).join("");
  const bad = parts.flatMap((r) => r.sources.filter((s) => !s.ok));
  if (!bad.length) return "";
  const names = [...new Set(bad.map((s) => sourceName(s.source)))];
  return `<p class="source-note">${bad.some((s) => s.stale) ? "Some figures are from the last good refresh" : "Some figures are missing"}: ${esc(names.join(", "))} didn't answer. <a href="#/${esc(code)}/sources">Sources tab</a></p>`;
}

const RANGES = [["1M", 30], ["3M", 91], ["6M", 182], ["1Y", 365], ["3Y", 1095], ["5Y", 1826], ["10Y", 3653]] as const;
const chartPrefs: PriceChartOptions & { range: string } = { style: "candles", sma50: true, sma200: true, from: 0, range: "1Y" };

const chartPanel: Panel = async (code, el) => {
  const r = await load(code, "prices");
  const bars: Bar[] = r.data?.bars ?? [];
  if (!bars.length) return sourceProblems(r) || notice("No price history.");
  if (current?.code !== code || current.tab !== "chart") return;
  el.innerHTML = `${sourceProblems(r)}
    <div class="toolbar">
      <div class="seg" id="ranges">${RANGES.map(([k]) => `<button data-range="${k}">${k}</button>`).join("")}</div>
      <div class="seg" id="opts">
        <button data-opt="style">${chartPrefs.style === "candles" ? "Candles" : "Line"}</button>
        <button data-opt="sma50" class="sma50">50d</button>
        <button data-opt="sma200" class="sma200">200d</button>
      </div>
    </div>
    <div class="chart" id="chart"></div>
    <dl class="stats" id="range-stats"></dl>
    <p class="muted small">Daily prices, last 10 years. Lines show the 50- and 200-day moving averages. Drag to pan, pinch to zoom.</p>`;
  const draw = () => {
    const days = RANGES.find(([k]) => k === chartPrefs.range)![1];
    chartPrefs.from = bars.at(-1)!.t - days * 86400;
    chart?.remove();
    chart = priceChart(document.getElementById("chart")!, bars, chartPrefs);
    el.querySelectorAll<HTMLButtonElement>("#ranges button").forEach((b) => b.classList.toggle("on", b.dataset.range === chartPrefs.range));
    el.querySelectorAll<HTMLButtonElement>("#opts button").forEach((b) => {
      const k = b.dataset.opt!;
      if (k === "style") b.textContent = chartPrefs.style === "candles" ? "Candles" : "Line";
      else b.classList.toggle("on", chartPrefs[k as "sma50" | "sma200"]);
    });
    const shown = bars.filter((b) => b.t >= chartPrefs.from);
    const ch = shown.length > 1 ? shown.at(-1)!.c / shown[0].c - 1 : null;
    const cur = r.data?.currency ?? "AUD";
    document.getElementById("range-stats")!.innerHTML =
      stat(`Change (${chartPrefs.range})`, pct(ch, true), tone(ch)) +
      stat("High", price(Math.max(...shown.map((b) => b.h)), cur)) +
      stat("Low", price(Math.min(...shown.map((b) => b.l)), cur)) +
      stat("Avg daily volume", compact(shown.reduce((a, b) => a + b.v, 0) / shown.length));
  };
  el.querySelector("#ranges")!.addEventListener("click", (e) => {
    const b = (e.target as HTMLElement).closest("button");
    if (b?.dataset.range) { chartPrefs.range = b.dataset.range; draw(); }
  });
  el.querySelector("#opts")!.addEventListener("click", (e) => {
    const k = (e.target as HTMLElement).closest("button")?.dataset.opt;
    if (k === "style") chartPrefs.style = chartPrefs.style === "candles" ? "line" : "candles";
    else if (k === "sma50" || k === "sma200") chartPrefs[k] = !chartPrefs[k];
    else return;
    draw();
  });
  draw();
};

const FIN_CHARTS: [string, string, "money" | "eps"][] = [
  ["TotalRevenue", "Revenue", "money"],
  ["NetIncome", "Net profit", "money"],
  ["DilutedEPS", "Earnings per share", "eps"],
  ["OperatingCashFlow", "Operating cash flow", "money"],
  ["FreeCashFlow", "Free cash flow", "money"],
  ["TotalDebt", "Total debt", "money"],
  ["CashAndCashEquivalents", "Cash", "money"],
  ["StockholdersEquity", "Shareholders' equity", "money"],
];

const fy = (d: string) => `FY${d.slice(2, 4)}`;

const financialsPanel: Panel = async (code, el) => {
  const r = await load(code, "financials");
  const f = r.data;
  if (!f) return sourceProblems(r) || notice("No financial statements available.");
  if (current?.code !== code || current.tab !== "financials") return;
  const cur = f.currency ?? "AUD";
  const charts = FIN_CHARTS.filter(([k]) => f.lines[k]?.length);
  const years = [...new Set(Object.values<{ date: string }[]>(f.lines).flat().map((p) => p.date))].sort();
  const rows = Object.entries<{ date: string; value: number }[]>(f.lines).map(([k, pts]) =>
    `<tr><th>${esc(humanize(k))}</th>${years.map((y) => {
      const v = pts.find((p) => p.date === y)?.value;
      return `<td class="${v != null && v < 0 ? "down" : ""}">${v == null ? "–" : k === "DilutedEPS" ? fixed(v, 3) : compact(v)}</td>`;
    }).join("")}</tr>`).join("");
  el.innerHTML = `${sourceProblems(r)}
    <p class="muted small">Annual reports, in ${esc(cur)}${cur !== "AUD" ? " (this company reports in a foreign currency)" : ""}. Financial years ending ${esc(years.at(-1)?.slice(5) ?? "")}.</p>
    <div class="grid">${charts.map(([k, label]) => card(label, `<div class="barchart" data-line="${k}"></div>`)).join("")}</div>
    ${card("All figures", `<div class="table-wrap"><table><thead><tr><th></th>${years.map((y) => `<th>${fy(y)}</th>`).join("")}</tr></thead><tbody>${rows}</tbody></table></div>`)}`;
  el.querySelectorAll<HTMLElement>(".barchart").forEach((c) => {
    const k = c.dataset.line!;
    const kind = FIN_CHARTS.find(([x]) => x === k)![2];
    const items = f.lines[k].map((p: { date: string; value: number }) => ({ label: fy(p.date), value: p.value }));
    // Currency is stated above the charts, so bar labels stay short (e.g. "63.2B").
    barChart(c, items, kind === "eps" ? (v) => fixed(v, 2) : (v) => compact(v), k === "TotalDebt" ? 3 : 1);
  });
};

const dividendsPanel: Panel = async (code, el) => {
  const [pr, pf] = await Promise.all([load(code, "prices"), load(code, "profile")]);
  const p = pr.data;
  if (!p) return sourceProblems(pr) || notice("No dividend data.");
  if (current?.code !== code || current.tab !== "dividends") return;
  const divs: { t: number; amount: number }[] = p.dividends ?? [];
  const cur = p.currency ?? "AUD";
  const last = p.price ?? p.bars?.at(-1)?.c;
  if (!divs.length) return `${sourceProblems(pr)}${notice("No dividends in the last 10 years.", "info")}`;
  const byYear = new Map<number, number>();
  for (const d of divs) {
    const y = new Date(d.t * 1000).getFullYear();
    byYear.set(y, (byYear.get(y) ?? 0) + d.amount);
  }
  const yearAgo = Date.now() / 1000 - 365 * 86400;
  const ttm = divs.filter((d) => d.t > yearAgo).reduce((a, d) => a + d.amount, 0);
  const sd = pf.data?.summary?.summaryDetail ?? {};
  const ce = pf.data?.summary?.calendarEvents ?? {};
  el.innerHTML = `${sourceProblems(pr)}
    <div class="grid">
      ${card("Summary", `<dl class="stats">
        ${stat("Last 12 months", price(ttm, cur))}
        ${stat("Trailing yield", pct(last ? ttm / last : null))}
        ${stat("Payout ratio", pct(n(sd.payoutRatio)))}
        ${stat("Next ex-dividend", ce.exDividendDate ? date(n(ce.exDividendDate)) : "–")}
      </dl><p class="muted small">Cash amounts per share, before franking credits (franking isn't in the free data).</p>`)}
      ${card("Per share, by calendar year", `<div class="barchart" id="div-bars"></div>`)}
      ${card("Payments", `<div class="table-wrap"><table><thead><tr><th>Ex-date</th><th>Amount</th></tr></thead><tbody>
        ${divs.slice().reverse().map((d) => `<tr><td>${date(d.t)}</td><td>${price(d.amount, cur)}</td></tr>`).join("")}</tbody></table></div>`)}
    </div>`;
  barChart(document.getElementById("div-bars")!, [...byYear].sort((a, b) => a[0] - b[0]).map(([y, v]) => ({ label: String(y).slice(2), value: v })), (v) => fixed(v, 2), 2);
};

let newsFilter: "all" | "sensitive" = "all";
const announcementsPanel: Panel = async (code, el) => {
  const r = await load(code, "announcements");
  const a = r.data;
  if (!a) return sourceProblems(r) || notice("No announcements.");
  if (current?.code !== code || current.tab !== "announcements") return;
  const draw = () => {
    const items = a.items.filter((i: any) => newsFilter === "all" || i.sensitive);
    el.innerHTML = `${sourceProblems(r)}
      <div class="toolbar"><div class="seg" id="news-filter">
        <button data-f="all" class="${newsFilter === "all" ? "on" : ""}">All</button>
        <button data-f="sensitive" class="${newsFilter === "sensitive" ? "on" : ""}">Price-sensitive</button>
      </div><a href="${esc(a.page)}" target="_blank" rel="noopener">All on asx.com.au ↗</a></div>
      <ul class="news">${items.map((i: any) => `<li>
        <a href="${esc(i.url ?? a.page)}" target="_blank" rel="noopener">
          <span class="when">${date(i.date)}${i.sensitive ? ` <b class="badge">Price-sensitive</b>` : ""}</span>
          <span class="headline">${esc(i.headline)}</span>
          ${i.size ? `<span class="muted small">PDF · ${esc(i.size)}</span>` : ""}
        </a></li>`).join("") || `<li class="muted">Nothing to show.</li>`}</ul>`;
    el.querySelector("#news-filter")!.addEventListener("click", (e) => {
      const f = (e.target as HTMLElement).closest("button")?.dataset.f as typeof newsFilter | undefined;
      if (f) { newsFilter = f; draw(); }
    });
  };
  draw();
};

const healthPanel: Panel = async (code) => {
  const [pr, pf, fi, an] = await Promise.all((["prices", "profile", "financials", "announcements"] as Part[]).map((x) => load(code, x)));
  const d: StockData = { prices: pr.data, profile: pf.data, financials: fi.data, announcements: an.data };
  const list = signals(d);
  if (!list.length) return notice("Not enough data to work out health signals.");
  const rated = list.filter((s) => s.rating !== "neutral");
  const good = rated.filter((s) => s.rating === "good").length;
  const groups = [...new Set(list.map((s) => s.group))];
  const score = rated.length ? good / rated.length : 0;
  return `
    <section class="card score">
      <div class="dial" style="--p:${(score * 100).toFixed(0)}"><span>${good}/${rated.length}</span></div>
      <div><h3>${score >= 0.66 ? "Mostly healthy" : score >= 0.4 ? "Mixed" : "Several warning signs"}</h3>
      <p class="muted">${good} of ${rated.length} rated signals are positive. Rule-based checks on public data: a starting point for research, not advice.</p></div>
    </section>
    <div class="grid">${groups.map((g) => card(g, `<ul class="signals">${list.filter((s) => s.group === g).map((s) => `
      <li class="${s.rating}"><span class="dot" aria-label="${s.rating}"></span>
        <div><b>${esc(s.name)}</b><span class="muted small">${esc(s.note)}</span></div>
        <span class="v">${esc(s.value)}</span></li>`).join("")}</ul>`)).join("")}</div>`;
};


const sourcesPanel: Panel = async (code) => {
  const all = await Promise.all((["prices", "profile", "financials", "announcements"] as Part[]).map((x) => load(code, x)));
  const statuses = all.flatMap((r) => r.sources);
  const errors = all.filter((r) => r.error).map((r) => notice(r.error!, "error")).join("");
  return `${errors}<ul class="sources">${statuses.map((s) => `<li class="${s.ok ? "good" : s.stale ? "neutral" : "caution"}">
    <span class="dot"></span>
    <div><b>${esc(sourceName(s.source))}</b>
      <span class="muted small">${s.ok ? "Working" : s.stale ? "Couldn't refresh: showing saved copy" : "Unavailable"} · ${s.fetchedAt ? `fetched ${dateTime(s.fetchedAt)}` : "never fetched"}</span>
      ${s.error ? `<span class="small down">${esc(s.error)}</span>` : ""}
      ${s.keys?.length ? `<details><summary class="small">${s.keys.length} fields</summary><code class="small">${esc(s.keys.join(", "))}</code></details>` : ""}
    </div></li>`).join("")}</ul>
    <p class="muted small">Data is saved for a while (15 minutes for prices, up to a day for financials) so pages load fast and don't overload the sources. These sources are free and unofficial, so they can change without notice.</p>`;
};

const RATING_LABELS: Record<string, string> = { strong_buy: "Strong buy", buy: "Buy", hold: "Hold", sell: "Sell", strong_sell: "Strong sell" };

interface Research { id: number; title: string; body: string; rating: string | null; targetPrice: number | null; updatedAt: string }

const researchPanel: Panel = async (code) => {
  let rows: Research[];
  try {
    rows = (await api<{ research: Research[] }>(`stock/${code}/research`)).research;
  } catch (e) {
    return notice((e as Error).message, "error");
  }
  if (!rows.length) {
    return `<section class="card"><h3>No research yet</h3><p>Ask Claude for a view on ${esc(code)} in your Claude app (with the Gold connector on) and ask it to save the analysis: it will appear here.</p>
      <p class="muted small">For a scored, dated assessment instead, see the <a href="#/${esc(code)}/assess">Assess tab</a>.</p></section>`;
  }
  return rows.map((r) => `<article class="card research">
      <header><h3>${esc(r.title)}</h3>
        <p class="muted small">${dateTime(r.updatedAt)}${r.rating ? ` · <b class="rating ${esc(r.rating)}">${esc(RATING_LABELS[r.rating] ?? r.rating)}</b>` : ""}${r.targetPrice ? ` · target ${price(r.targetPrice)}` : ""}</p></header>
      <div class="md">${markdown(r.body)}</div></article>`).join("");
};

// "Your position" card on the overview, when the owner holds the stock.
async function positionCard(code: string): Promise<string> {
  if (current?.sim) return ""; // the simulator shows its own position
  const p = await loadPortfolio().catch(() => null);
  const h = p?.holdings.find((x) => x.code === code);
  if (!h) return "";
  return card("Your position", `<dl class="stats">
    ${stat("Units", fixed(h.units, h.units % 1 ? 2 : 0))}
    ${stat("Average cost", price(h.avgCost))}
    ${stat("Value", money(h.value))}
    ${stat("Profit", `${h.unrealised != null && h.unrealised > 0 ? "+" : ""}${money(h.unrealised)} (${pct(h.unrealisedPct, true)})`, tone(h.unrealised))}
    ${stat("Share of portfolio", pct(h.weight))}
    ${stat("Held since", date(h.firstBought))}
  </dl>`);
}

const PANELS: Record<Tab, Panel> = {
  overview, chart: chartPanel, financials: financialsPanel, dividends: dividendsPanel,
  announcements: announcementsPanel, research: researchPanel, health: healthPanel, sources: sourcesPanel,
  assess: (code) => assessPanel(code),
  calendar: calendarPanel, docs: docsPanel,
};

/** A stock tab drawn inside the simulator's stock page (same panels as the live site). */
export const SIM_TABS: [Tab, string][] = [["overview", "Overview"], ["chart", "Chart"], ["financials", "Financials"], ["dividends", "Dividends"], ["announcements", "News"]];
async function simPanel(code: string, tab: string, el: HTMLElement) {
  const t = (SIM_TABS.find(([id]) => id === tab)?.[0] ?? "overview") as Tab;
  chart?.remove();
  chart = null;
  current = { code, tab: t, token: ++renderToken, sim: true };
  const token = renderToken;
  el.innerHTML = spinner;
  const html = await PANELS[t](code, el);
  if (token === renderToken && typeof html === "string") el.innerHTML = html;
}

// ---------- routing ----------

// Phones: the menu opens from ☰ in the top bar, under the header, and closes on a pick, a tap
// outside it or Escape. (Wider screens show it as a row all the time.)
const menuBtn = document.getElementById("menu-btn") as HTMLButtonElement;
const nav = document.getElementById("mainnav")!;
function closeMenu() { document.body.classList.remove("menu-open"); menuBtn.setAttribute("aria-expanded", "false"); }
menuBtn.addEventListener("click", (e) => {
  e.stopPropagation();
  const open = !document.body.classList.contains("menu-open");
  nav.style.top = `${(document.querySelector("header.top") as HTMLElement).getBoundingClientRect().bottom + 6}px`;
  document.body.classList.toggle("menu-open", open);
  menuBtn.setAttribute("aria-expanded", String(open));
});
document.addEventListener("click", (e) => { if (document.body.classList.contains("menu-open") && !nav.contains(e.target as Node)) closeMenu(); });
document.addEventListener("keydown", (e) => { if (e.key === "Escape") closeMenu(); });
let badgesAt = 0;
function refreshBadges() {
  if (simUser || Date.now() - badgesAt < 60_000) return;
  badgesAt = Date.now();
  api<{ unread: number }>("badges").then(({ unread }) => {
    for (const id of ["res-badge", "menu-badge"]) {
      const b = document.getElementById(id)!;
      b.hidden = !unread;
      b.title = `${unread} unread resource${unread === 1 ? "" : "s"}`;
    }
  }).catch(() => {});
}

function route() {
  const [path, query = ""] = location.hash.split("?");
  const [, rawCode, rawTab, ...rest] = path.split("/");
  if (simUser && rawCode !== "simulator") { location.replace("#/simulator"); return; } // Gold logins: the simulator only
  document.body.classList.toggle("in-chat", rawCode === "chat");
  document.body.classList.toggle("in-portfolio", rawCode === "portfolio");
  document.body.classList.toggle("in-leads", rawCode === "leads");
  // The simulator is its own part of the app: its own menu, and the brand and search stay inside it.
  const inSim = rawCode === "simulator";
  document.body.classList.toggle("in-sim", inSim);
  document.querySelector<HTMLAnchorElement>(".brand")!.href = inSim ? "#/simulator" : "#/";
  // Highlight the current section in the main menu (stock pages count as Home).
  const name = rawCode === "inbox" ? "resources" : rawCode === "watchlist" ? "watchlists" : rawCode ?? "";
  const section = ["portfolio", "watchlists", "leads", "reviews", "analytics", "resources", "settings", "simulator"].includes(name) ? name : "home";
  const simView = ["account", "trade", "watchlist", "orders", "journal", "me"].includes(rawTab) ? rawTab : rawTab === "stock" ? "" : "account";
  const active = inSim ? `sim-${simView}` : section;
  document.querySelectorAll<HTMLElement>("#mainnav a").forEach((a) => (a.dataset.route === active ? a.setAttribute("aria-current", "page") : a.removeAttribute("aria-current")));
  closeMenu();
  refreshBadges();
  if (rawCode === "connect") { current = null; return renderConnect(app, query); }
  if (rawCode === "portfolio") { current = null; return renderPortfolio(app); }
  if (rawCode === "leads") { current = null; return renderLeads(app); }
  if (rawCode === "analytics") { current = null; return renderAnalytics(app); }
  if (rawCode === "resources" || rawCode === "inbox") { current = null; return renderInbox(app); } // #/inbox is the old address
  if (rawCode === "reviews") { current = null; return renderReviews(app, query); }
  if (rawCode === "market") { current = null; return renderMarket(app, query); }
  if (name === "settings") { current = null; return renderSettings(app, rawTab); }
  if (name === "simulator") { current = null; chart?.remove(); chart = null; return renderSim(app, rawTab ?? "", query, rest, { tabs: SIM_TABS, panel: simPanel }); }
  if (name === "watchlists") { current = null; return renderWatchlists(app, query, (c) => load(c, "prices")); }
  if (rawCode === "chat") {
    current = null;
    chart?.remove();
    chart = null;
    const about = new URLSearchParams(query).get("about")?.toUpperCase();
    const draft = about && /^[A-Z0-9]{3,6}$/.test(about)
      ? `What do you think of ${about} right now? Cover its health, valuation, dividends and any recent news.`
      : "";
    return renderChat(app, rawTab || null, draft);
  }
  const code = rawCode?.toUpperCase();
  if (!code || !/^[A-Z0-9]{3,6}$/.test(code)) return renderHome();
  const tab = (TABS.find(([id]) => id === rawTab)?.[0] ?? "overview") as Tab;
  (search.elements.namedItem("code") as HTMLInputElement).value = "";
  renderStock(code, tab);
}

search.addEventListener("submit", (e) => {
  e.preventDefault();
  const input = search.elements.namedItem("code") as HTMLInputElement;
  const code = input.value.trim().toUpperCase().replace(/\.AX$/, "");
  if (!/^[A-Z0-9]{3,6}$/.test(code)) { input.setCustomValidity("Enter an ASX code, like BHP"); input.reportValidity(); return; }
  input.setCustomValidity("");
  input.blur();
  location.hash = document.body.classList.contains("in-sim") ? `#/simulator/stock/${code}` : `#/${code}`;
});
(search.elements.namedItem("code") as HTMLInputElement).addEventListener("input", (e) => (e.target as HTMLInputElement).setCustomValidity(""));

window.addEventListener("hashchange", () => (location.hash.startsWith("#/join") ? start() : document.body.classList.contains("signed-out") || route()));

// Anything marked with data-stock (chips, picks inside a <summary>…) opens that stock's page.
const openStock = (e: Event) => {
  const el = (e.target as HTMLElement).closest<HTMLElement>("[data-stock]");
  if (!el) return;
  const control = (e.target as HTMLElement).closest("a, button, select, input, textarea");
  if (control && el.contains(control)) return; // a real control inside it does its own thing
  if (e instanceof KeyboardEvent && e.key !== "Enter") return;
  e.preventDefault();
  e.stopPropagation();
  location.hash = `#/${el.dataset.stock}`;
};
document.addEventListener("click", openStock, true);
document.addEventListener("keydown", openStock, true);

// ---------- refreshing ----------

function toast(message: string, kind = "") {
  document.querySelector(".toast")?.remove();
  document.body.insertAdjacentHTML("beforeend", `<div class="toast ${kind}" role="status">${esc(message)}</div>`);
  const el = document.querySelector<HTMLElement>(".toast")!;
  setTimeout(() => el.remove(), 4000);
}

// Draw the current screen again with fresh data (not the chat or the connect screen, which hold input).
function redraw() {
  const route0 = location.hash.split("/")[1]?.split("?")[0];
  if (document.body.classList.contains("signed-out") || route0 === "chat" || route0 === "connect" || location.hash.startsWith("#/simulator/trade")) return;
  store.clear();
  const go = () => { current = null; route(); };
  if (simUser) return go();
  loadPortfolio(true).catch(() => {});
  refreshMarks().catch(() => {}).finally(go); // marks first, so codes show what changed (e.g. a trade Claude recorded)
}

// The ↻ button: fetch this stock again from its sources (or, elsewhere, your holdings and watchlist).
refreshButton.addEventListener("click", async () => {
  if (refreshButton.classList.contains("busy")) return;
  refreshButton.classList.add("busy");
  const code = current?.code;
  if (simUser || (document.body.classList.contains("in-sim") && !code)) { // nothing live to refresh: just draw the simulator again
    redraw();
    refreshButton.classList.remove("busy");
    return;
  }
  try {
    const r = await api<{ refreshed: { codes: string[]; failed: string[] } }>("refresh", "POST", code ? { code } : {});
    const { codes, failed } = r.refreshed;
    if (failed.length) toast(`Couldn't refresh ${failed.join(", ")}: showing the last saved data.`, "warn");
    else toast(code ? `${code} is up to date.` : codes.length ? `Updated ${codes.length} stock${codes.length > 1 ? "s" : ""}: ${codes.join(", ")}.` : "Nothing to refresh yet: add holdings or watchlist stocks.");
    redraw();
  } catch (e) {
    toast((e as Error).message, "error");
  } finally {
    refreshButton.classList.remove("busy");
  }
});

// While the ASX is open, the home and portfolio screens redraw every 5 minutes; any screen redraws
// when you come back to it after 10 minutes away. (The scheduled refresh keeps the data itself fresh.)
const asxOpen = () => {
  const p = Object.fromEntries(new Intl.DateTimeFormat("en-AU", { timeZone: "Australia/Sydney", weekday: "short", hour: "2-digit", minute: "2-digit", hourCycle: "h23" })
    .formatToParts(new Date()).map((x) => [x.type, x.value]));
  const m = Number(p.hour) * 60 + Number(p.minute);
  return !["Sat", "Sun"].includes(p.weekday) && m >= 600 && m < 990;
};
setInterval(() => {
  const r = location.hash.split("/")[1]?.split("?")[0] ?? "";
  if (document.visibilityState === "visible" && asxOpen() && (r === "" || r === "portfolio" || (r === "simulator" && !/trade/.test(location.hash)))) redraw();
}, 5 * 60 * 1000);
let hiddenAt = 0;
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "hidden") hiddenAt = Date.now();
  else if (hiddenAt && Date.now() - hiddenAt > 10 * 60 * 1000) redraw();
});
matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => current && !current.sim && renderStock(current.code, current.tab));

async function start() {
  if (location.hash.startsWith("#/join")) return renderJoin(app, new URLSearchParams(location.hash.split("?")[1] ?? "").get("token") ?? "", start);
  const { data } = await supabase.auth.getSession();
  if (!data.session) {
    // A Gold login (cookie)?
    const me = await api<{ kind: string }>("me").catch(() => null);
    if (me?.kind !== "user") return renderSignIn();
    simUser = true;
    document.body.classList.add("sim-user");
    document.body.classList.remove("signed-out");
    return route();
  }
  try {
    await Promise.all([refreshWatchlist(), refreshMarks()]);
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) return renderSignIn();
    if (e instanceof ApiError && e.status === 403) {
      await supabase.auth.signOut();
      return renderSignIn(e.message);
    }
    app.innerHTML = notice(e instanceof Error ? e.message : String(e), "error");
    return;
  }
  document.body.classList.remove("signed-out");
  route();
}

// Long-press or right-click any stock code (on the live side, for staff) for open / watch / blacklist.
installQuickMenu(() => !simUser && !document.body.classList.contains("in-sim") && !document.body.classList.contains("signed-out"), () => { refreshWatchlist().catch(() => {}); redraw(); });

start();
